import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { FilterOption } from 'src/components/searchbox'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import { BillingService } from '../../billing/bl_service'
import { DEFAULT_TIERS, PriceTierEditorPresenter } from '../../billing/bl_price_tier_editor_presenter'
import type { PriceTierInput } from '../../billing/bl_types'
import type { UserRow } from '../../user-manager/um_scopes'
import { UserManagerService } from '../../user-manager/um_service'
import GroupManagerKeys from '../gm_keys'
import { GroupRow, HeaderScope } from '../gm_scopes'
import { GroupManagerService } from '../gm_service'
import { GroupManagerFormKeys } from './gm_form_keys'
import GroupManagerFormScope, { type GroupFormTab } from './gm_form_scope'

const LOG = Logger.get('GroupManagerFormPresenter')
const groupManagerService = GroupManagerService.singleton()
const userManagerService = UserManagerService.singleton()
const billingService = BillingService.singleton()

export class GroupManagerFormPresenter extends CubePresenter<MainPresenter, GroupManagerFormScope> {
    private readonly __headerBar: Presenter<HeaderScope, GroupManagerFormPresenter>

    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new GroupManagerFormScope())
        this.__headerBar = new Presenter(this, this.scope.header)
        this.__billingTierEditor = new PriceTierEditorPresenter(this, this.scope.billingTierEditor, {
            onChanged: () => this.markDirty()
        })
        this.__billingDefaultTierEditor = new PriceTierEditorPresenter(this, this.scope.billingDefaultTierEditor, {
            readOnly: true
        })
    }

    private readonly __billingTierEditor: PriceTierEditorPresenter

    private readonly __billingDefaultTierEditor: PriceTierEditorPresenter

    // :: Instance
    #groupId?: number

    #baseline?: GroupFormBaseline

    markDirty() {
        this.scope.dirty = true
    }

    #resetForm() {
        this.#baseline = {
            formData: { ...this.scope.formData, users: [...(this.scope.formData.users ?? [])] },
            billingUseDefault: this.scope.billingUseDefault,
            billingTiers: this.scope.billingTierEditor.tiers.map((tier) => ({ ...tier }))
        }
        this.scope.dirty = false
        this.scope.formVersion++
    }

    // :: Methods

    buscarGrupoSelecionado = async (groupId?: number) => {
        if (!groupId) return

        try {
            this.scope.waiting = new WaitingScope()
            LOG.debug(`buscarGrupoSelecionado ${groupId}`)
            let group = await groupManagerService.fetchGroupById(groupId)
            if (group) {
                group = {
                    ...group,
                    ta_sb_active_filters: group.ta_sb_active_filters ?? {},
                    tc_sb_active_filters: group.tc_sb_active_filters ?? {},
                    tcm_sb_active_filters: group.tcm_sb_active_filters ?? {}
                }
                this.scope.onFormDataChange(group)
                this.scope.groupEditing = group
                this.#groupId = groupId
            }

            await this.#loadBilling(groupId)
            this.#resetForm()
        } finally {
            this.scope.waiting = undefined
        }
    }

    async #loadBilling(groupId?: number) {
        try {
            const [groupTable, defaultTable] = await Promise.all([
                groupId ? billingService.fetchPriceTable(groupId) : Promise.resolve(null),
                billingService.fetchPriceTable(null)
            ])

            const defaultTiers = defaultTable?.tiers?.length ? defaultTable.tiers.map(toTierInput) : [...DEFAULT_TIERS]
            this.__billingDefaultTierEditor.setTiers(defaultTiers)

            const hasOwnTable = !!groupTable?.tiers?.length
            this.scope.billingUseDefault = !hasOwnTable
            this.__billingTierEditor.setTiers(
                hasOwnTable ? (groupTable as { tiers: PriceTierInput[] }).tiers.map(toTierInput) : [...defaultTiers]
            )
            this.__billingTierEditor.clearErrors()
        } catch (caught) {
            LOG.error('loadBilling', caught)
            this.__billingDefaultTierEditor.setTiers([...DEFAULT_TIERS])
            this.scope.billingUseDefault = true
            this.__billingTierEditor.setTiers([...DEFAULT_TIERS])
        }
    }

    async #saveBilling(groupId: number) {
        if (this.scope.billingUseDefault) {
            await billingService.resetGroupPriceTable(groupId)
            return
        }

        await billingService.savePriceTable({ groupId, tiers: this.scope.billingTierEditor.tiers })
    }

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new GroupManagerFormKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        LOG.debug(`applyParameters ${keys.groupId}`)
        if (initialization) {
            await this.#initialize()
        }

        if (keys.groupId && this.#groupId !== keys.groupId) {
            await this.buscarGrupoSelecionado(keys.groupId)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new GroupManagerFormKeys(this.app, intent)
        keys.groupId = this.#groupId
        LOG.debug(`publishParameters ${keys.groupId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onAddUserToGroup = this.action(this.handleAddUserToGroup)
        this.scope.onRemoveUserFromGroup = this.action(this.handleRemoveUserFromGroup)
        this.scope.onSetUserToAdmin = this.action(this.handleSetUserToAdmin)

        this.scope.onClose = this.action(this.handleClose)

        this.scope.onSave = this.action(this.handleCheckSave)

        this.scope.onFormDataChange = this.action(this.handleFormDataChange)

        this.scope.onConfirmStatusChanged = this.action(this.handleSave)

        this.scope.onCloseModalStatusChanged = this.action(this.handleCloseModalStatusChanged)

        this.scope.onBillingUseDefaultChanged = this.action(this.handleBillingUseDefaultChanged)

        this.scope.onTabSelected = this.action(this.handleTabSelected)
        this.scope.onDiscard = this.action(this.handleDiscard)
        this.scope.onSelectedUserChanged = this.action(this.handleSelectedUserChanged)
        this.scope.onOpenRowMenu = this.action(this.handleOpenRowMenu)
        this.scope.onCloseRowMenu = this.action(this.handleCloseRowMenu)

        await this.#loadBilling(this.#groupId)
        this.#resetForm()

        this.scope.allUsers = await userManagerService.fetchUsers()

        if (!this.scope.ta_filter_menu_options_array.length) {
            this.__initializeFilterMenuOptionsArray()
        }
    }

    private __initializeFilterMenuOptionsArray() {
        const tcExclusion = new Set<string>([
            'ato',
            'ato.cbo',
            'cid10',
            'med.esp',
            'ops.emp',
            'ops.prf',
            'med.crm',
            'med.crm.uf',
            'crm.dta.inscricao',
            'emp.nat',
            'estab.tipo',
            'esa.complex',
            'esa.leitos.porte',
            'esa.leitos.qtd',
            'emp.uf',
            'estab.uf'
        ])
        const tmExclusion = new Set<string>([
            'ops.emp',
            'ops.prf',
            'med.crm',
            'med.crm.uf',
            'crm.dta.inscricao',
            'emp.nat',
            'estab.tipo',
            'esa.complex',
            'esa.leitos.porte',
            'esa.leitos.qtd',
            'emp.uf',
            'estab.uf'
        ])
        const taExclusion = new Set<string>(['socio'])

        const push = (option: FilterOption) => {
            if (!tcExclusion.has(option.key)) {
                this.scope.tc_filter_menu_options_array.push(option)
            }

            if (!tmExclusion.has(option.key)) {
                this.scope.tm_filter_menu_options_array.push(option)
            }

            if (!taExclusion.has(option.key)) {
                if (option.label.startsWith('Pessoa ::')) {
                    const taOption: FilterOption = { ...option }
                    if (taOption.key === 'pessoa' || taOption.key === 'pes.cbo') {
                        taOption.compliance = false
                    }
                    taOption.label = `Médico ::${taOption.label.substring(9)}`
                    this.scope.ta_filter_menu_options_array.push(taOption)
                } else {
                    this.scope.ta_filter_menu_options_array.push(option)
                }
            }
        }

        push({ key: '_', label: 'Livre' })
        push({ key: 'empresa', label: 'Empresa :: Nome' })
        push({ key: 'cnpj', label: 'Empresa :: CNPJ' })
        push({ key: 'emp.nat', label: 'Empresa :: Nat.Jurídica' })
        push({ key: 'emp.endereco', label: 'Empresa :: Endereço' })
        push({ key: 'emp.cidade', label: 'Empresa :: Cidade' })
        push({ key: 'emp.bairro', label: 'Empresa :: Bairro' })
        push({ key: 'emp.rua', label: 'Empresa :: Rua' })
        push({ key: 'emp.cep', label: 'Empresa :: CEP' })
        push({ key: 'emp.uf', label: 'Empresa :: UF' })
        push({ key: 'cnae', label: 'Empresa :: CNAE' })
        push({ key: 'socio', label: 'Sócio :: Nome' })
        push({ key: 'pessoa', label: 'Pessoa :: Nome', compliance: true })
        push({ key: 'pes.cpf', label: 'Pessoa :: CPF' })

        this.scope.ta_filter_menu_options_array.push({
            key: 'pes.cpf_block_display_full_list',
            label: 'Médico :: CPF :: Bloquear exibição lista',
            compliance: true
        })

        push({ key: 'pes.endereco', label: 'Pessoa :: Endereço', compliance: true })
        push({ key: 'pes.cidade', label: 'Pessoa :: Cidade', compliance: true })
        push({ key: 'pes.bairro', label: 'Pessoa :: Bairro', compliance: true })
        push({ key: 'pes.rua', label: 'Pessoa :: Rua', compliance: true })
        push({ key: 'pes.cep', label: 'Pessoa :: CEP', compliance: true })
        push({ key: 'pes.cbo', label: 'Pessoa :: CBO', compliance: true })
        push({ key: 'ato.cbo', label: 'Pessoa :: CBO do Ato' })
        push({ key: 'ato', label: 'Pessoa :: ATO' })
        push({ key: 'cid10', label: 'Pessoa :: CID10' })
        push({ key: 'med.esp', label: 'Médico :: Especialidade' })
        push({ key: 'med.crm', label: 'Médico :: CRM' })
        push({ key: 'med.crm.uf', label: 'Médico :: CRM :: UF' })
        push({ key: 'crm.dta.inscricao', label: 'Médico :: CRM :: Data Inscrição' })

        push({ key: 'estab', label: 'E.Saúde :: Nome' })
        push({ key: 'cnes', label: 'E.Saúde :: CNES' })
        push({ key: 'estab.tipo', label: 'E.Saúde :: Tipo' })
        push({ key: 'esa.complex', label: 'E.Saúde :: Complexidade' })
        push({ key: 'esa.leitos.porte', label: 'E.Saúde :: Leitos Porte' })
        push({ key: 'esa.leitos.qtd', label: 'E.Saúde :: Leitos Quantidade' })
        push({ key: 'estab.endereco', label: 'E.Saúde :: Endereço' })
        push({ key: 'estab.cidade', label: 'E.Saúde :: Cidade' })
        push({ key: 'estab.bairro', label: 'E.Saúde :: Bairro' })
        push({ key: 'estab.rua', label: 'E.Saúde :: Rua' })
        push({ key: 'estab.cep', label: 'E.Saúde :: CEP' })
        push({ key: 'estab.uf', label: 'E.Saúde :: UF' })

        push({ key: 'ops.emp', label: 'Operadora :: Empresa/E.Saúde' })
        push({ key: 'ops.prf', label: 'Operadora :: Profissional' })
    }

    private async handleClose() {
        await new GroupManagerKeys(this.app).flip()
    }

    private async handleRemoveUserFromGroup(userId: number) {
        this.scope.rowMenuUserId = undefined
        this.markDirty()
        this.scope.waiting = new WaitingScope()

        this.scope.formData.users = this.scope.formData.users?.filter((u) => u.id !== userId)
        if (this.scope.formData.admin_id === userId) {
            this.scope.formData.admin_id = undefined
            this.scope.formData.admin_name = undefined
            this.scope.formData.admin_fullname = undefined
        }

        this.scope.waiting = undefined
    }

    private async handleAddUserToGroup() {
        const user = this.scope.allUsers.find((u) => u.id === this.scope.selectedUser?.id)
        if (!user) {
            this.app.notify('warning', 'Selecione um usuário para adicionar ao grupo')
            return
        }

        if (!this.scope.formData.users) {
            this.scope.formData.users = []
        }
        const alreadyInGroup = this.scope.formData.users.find((u) => u.id === user.id)
        if (alreadyInGroup) {
            this.app.notify('warning', 'Usuário já está no grupo')
            return
        }

        this.scope.waiting = new WaitingScope()

        this.scope.formData.users = [
            ...this.scope.formData.users,
            {
                id: user.id!,
                name: user.name,
                full_name: user.full_name,
                can_export_data: user.can_export_data
            }
        ]

        this.scope.selectedUser = undefined
        this.markDirty()

        this.scope.waiting = undefined
    }

    private async handleSetUserToAdmin(userId: number) {
        this.scope.rowMenuUserId = undefined
        const user = this.scope.formData.users?.find((u) => u.id === userId)
        if (!user) {
            this.app.notify('warning', 'Usuário não encontrado no grupo')
            return
        }
        this.scope.waiting = new WaitingScope()
        this.scope.formData.admin_id = userId
        this.scope.formData.admin_name = user.name
        this.scope.formData.admin_fullname = user.full_name
        this.markDirty()
        this.scope.waiting = undefined
    }

    private async handleCloseModalStatusChanged() {
        this.scope.confirmStatusChangedModalOpen = false
    }

    private async handleCheckSave() {
        if (!this.scope.formData.name) {
            this.app.notify('warning', 'Nome do grupo deve ser informado')
            return
        }

        if (!this.scope.billingUseDefault) {
            const errors = this.__billingTierEditor.validate()
            if (errors.length > 0) {
                this.app.notify('warning', 'Corrija as faixas de faturamento antes de salvar')
                return
            }
        }

        if (this.scope.groupEditing?.active !== this.scope.formData.active) {
            this.scope.confirmStatusChangedModalOpen = true
            return
        }

        await this.handleSave()
    }

    private async handleSave() {
        try {
            this.scope.confirmStatusChangedModalOpen = false
            this.scope.waiting = new WaitingScope()
            const retSalvar = this.scope.formData.id
                ? await groupManagerService.updateGroup(this.scope.formData.id, this.scope.formData)
                : await groupManagerService.createGroup(this.scope.formData)

            if (retSalvar) {
                const groupId = this.scope.formData.id ?? retSalvar.id
                if (groupId) {
                    this.#groupId = groupId
                    await this.#saveBilling(groupId)
                }

                await this.buscarGrupoSelecionado(this.#groupId)

                this.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleBillingUseDefaultChanged(useDefault: boolean) {
        this.scope.billingUseDefault = useDefault
        this.markDirty()

        if (useDefault) {
            this.__billingTierEditor.clearErrors()
            return
        }

        if (this.scope.billingTierEditor.tiers.length === 0) {
            const defaultTiers = this.scope.billingDefaultTierEditor.tiers
            this.__billingTierEditor.setTiers(defaultTiers.length ? [...defaultTiers] : [...DEFAULT_TIERS])
        } else {
            this.__billingTierEditor.validate()
        }
    }

    private async handleTabSelected(tab: GroupFormTab) {
        this.scope.activeTab = tab
    }

    private async handleDiscard() {
        const baseline = this.#baseline
        if (!baseline) {
            return
        }

        this.scope.formData = { ...baseline.formData, users: [...(baseline.formData.users ?? [])] }
        this.scope.billingUseDefault = baseline.billingUseDefault
        this.__billingTierEditor.setTiers(baseline.billingTiers.map((tier) => ({ ...tier })))
        this.#resetForm()
    }

    private async handleSelectedUserChanged(user: UserRow | undefined) {
        this.scope.selectedUser = user
    }

    private async handleOpenRowMenu(userId: number) {
        this.scope.rowMenuUserId = userId
    }

    private async handleCloseRowMenu() {
        this.scope.rowMenuUserId = undefined
    }

    private async handleFormDataChange(dadosAlterados: Partial<GroupRow>) {
        this.markDirty()
        this.scope.formData = {
            ...this.scope.formData,
            ...dadosAlterados
        }
    }
}

type GroupFormBaseline = {
    formData: GroupRow
    billingUseDefault: boolean
    billingTiers: PriceTierInput[]
}

function toTierInput(tier: PriceTierInput): PriceTierInput {
    return { minQty: tier.minQty, maxQty: tier.maxQty ?? null, unitPrice: tier.unitPrice }
}

export default GroupManagerFormPresenter
